import React from 'react';
import { cn } from '@/lib/utils';

interface TonedImageIconProps {
  src: string;
  alt: string;
  className?: string;
  /** Fall back to filter if mask fails (e.g., JPG without transparency) */
  fallbackToFilter?: boolean;
}

/**
 * TonedImageIcon - Renders custom uploaded icons in the same grey tone as Lucide icons
 * 
 * Uses CSS mask-image to apply the parent's text color to the icon, ensuring
 * perfect color matching with Lucide icons (which use currentColor).
 * 
 * For icons without transparency (JPG), falls back to a CSS filter.
 * Format detection is done from URL to avoid double-loading images.
 */
const TonedImageIcon: React.FC<TonedImageIconProps> = ({
  src,
  alt,
  className,
  fallbackToFilter = true
}) => {
  // Detect format from URL string (no hidden image needed)
  const srcLower = src.toLowerCase();
  const isJpeg = srcLower.includes('.jpg') || srcLower.includes('.jpeg') || srcLower.includes('image/jpeg');
  const useFilter = fallbackToFilter && isJpeg;

  // Grey CSS filter for non-transparent images (JPG, etc.)
  const greyFilter = 'brightness(0) saturate(100%) invert(35%) sepia(0%) saturate(0%) brightness(55%) contrast(90%)';

  if (useFilter) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn('w-full h-full object-contain', className)}
        style={{ filter: greyFilter }}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className={cn('w-full h-full bg-current', className)}
      style={{
        WebkitMaskImage: `url(${src})`,
        WebkitMaskSize: 'contain',
        WebkitMaskPosition: 'center',
        WebkitMaskRepeat: 'no-repeat',
        maskImage: `url(${src})`,
        maskSize: 'contain',
        maskPosition: 'center',
        maskRepeat: 'no-repeat'
      }}
      role="img"
      aria-label={alt}
    />
  );
};

export default TonedImageIcon;
